import { PropsWithChildren, Ref, RefObject, forwardRef, useCallback, useEffect, useRef, useState } from 'react'

import ScrollShadowWrapper from '@/modules/shared/components/scroll-shadow/ScrollShadowWrapper'
import TableControl from '@/modules/shared/components/table/TableControl'
import TableHeader from '@/modules/shared/components/table/TableHeader'
import TableMultiRowControl from '@/modules/shared/components/table/TableMultiRowControl'
import { TableNoSubLines } from '@/modules/shared/components/table/TableNoSubLines'
import TableOverlayLoading from '@/modules/shared/components/table/TableOverlayLoading'
import TableRow from '@/modules/shared/components/table/TableRow'
import { TableSubLine } from '@/modules/shared/components/table/TableSubLine'
import { PaginationDefinition } from '@/modules/shared/components/table/types'
import { classNames } from '@/modules/shared/utils/classNames'

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (!ref) return
  if (typeof ref === 'function') {
    ref(value)
  } else {
    ;(ref as RefObject<T | null>).current = value
  }
}

interface TableWrapperProps extends PropsWithChildren {
  dataTestId?: string
  loading?: boolean
  hasData?: boolean
  className?: string
  pagination?: PaginationDefinition
  /** Cap grid height with vertical scroll so horizontal scrollbars stay reachable; pagination stays outside the scroll area. */
  constrainScrollToViewport?: boolean
  /** When set, applies an inline max-height so the grid fits above bottom pagination on any resolution. */
  scrollMaxHeightPx?: number
}

const TableComponent = forwardRef<HTMLDivElement, TableWrapperProps>(function TableComponent(
  {
    dataTestId,
    loading,
    hasData,
    children,
    className,
    pagination,
    constrainScrollToViewport = false,
    scrollMaxHeightPx,
  },
  forwardedRef
) {
  const [isScrollbarPresent, setIsScrollbarPresent] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const tableMeasureRef = useRef<HTMLTableElement>(null)

  const setScrollRef = useCallback(
    (el: HTMLDivElement | null) => {
      scrollContainerRef.current = el
      assignRef(forwardedRef, el)
    },
    [forwardedRef]
  )

  const onCheckScrollbarPresence = useCallback(() => {
    const el = scrollContainerRef.current
    if (!el) return
    const isPresent = el.scrollWidth > el.clientWidth
    setIsScrollbarPresent(isPresent)
  }, [])

  useEffect(() => {
    if (!hasData) return
    const scrollEl = scrollContainerRef.current
    const tableEl = tableMeasureRef.current
    if (!scrollEl) return

    onCheckScrollbarPresence()
    const ro = new ResizeObserver(onCheckScrollbarPresence)
    ro.observe(scrollEl)
    if (tableEl) {
      ro.observe(tableEl)
    }
    window.addEventListener('resize', onCheckScrollbarPresence)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', onCheckScrollbarPresence)
    }
  }, [hasData, loading, onCheckScrollbarPresence])
  if (!hasData) return null

  const measuredScroll = typeof scrollMaxHeightPx === 'number'
  const scrollStyle = measuredScroll ? { maxHeight: scrollMaxHeightPx } : undefined

  return (
    <ScrollShadowWrapper
      ref={setScrollRef}
      direction="horizontal"
      overflowBothAxes={constrainScrollToViewport}
      style={scrollStyle}
      className={classNames(
        { 'table-scrollbar border-b': isScrollbarPresent && pagination },
        measuredScroll && 'table-measured-scroll'
      )}
      testId={dataTestId}
    >
      <TableOverlayLoading loading={loading} />
      <table
        ref={tableMeasureRef}
        className={classNames(
          'w-full border-collapse bg-white text-left text-sm',
          {
            'opacity-50': loading,
          },
          className
        )}
      >
        {children}
      </table>
    </ScrollShadowWrapper>
  )
})

TableComponent.displayName = 'TableWrapper'

export const TableWrapper = Object.assign(TableComponent, {
  Header: TableHeader,
  Loading: TableOverlayLoading,
  Row: TableRow,
  SubLine: TableSubLine,
  NoSubLines: TableNoSubLines,
  Control: TableControl,
  MultiRowControl: TableMultiRowControl,
})
